import { Menu } from '@mantine/core'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { FIXED_CHILDREN_MODAL_TRANSITION } from 'components/AboveAllModalOverlay/fixedChildrenModalTransition'
import { WideHeader } from 'components/ModalDrawer/WideHeader/WideHeader'
import { translate } from 'i18n/i18n'
import type { FC } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import { captureFrontendError } from 'utils/captureFrontendError'
import { showNotification } from '../../../utils/showNotification'
import { selectedPropertyHasPermission } from '../../Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { openNodeRemoveModal } from '../NodeRemoveModal/NodeRemoveModal'
import { openNodeSwapModal } from '../NodeSwapModal/NodeSwapModal'
import { serviceCenterStore } from '../store/serviceCenterStore'
import type { TActiveGateway, TActiveNode, TNodeServiceIssueSummary } from '../types'
import { TServiceIssueStatus } from '../types'
import { serviceCenterNodeServiceIssuesGet } from '../utils/api/serviceCenterNodeServiceIssuesGet'
import { getNodeTypeLabelFromNode } from '../utils/getNodeTypeLabel'
import { ServiceIssueDetail } from './IssueDetail/IssueDetail'
import { ServiceIssueList } from './List/List'

const MODAL_ID = 'node-maintenance'

export const checkHasIssueUpdatePermission = () =>
  selectedPropertyHasPermission({ permission: 'EDIT_SSC_NODE_ISSUE_STATUS' })

export const openNodeMaintenanceModal = (node: TActiveNode | TActiveGateway, serviceIssueId?: number) => {
  AboveAllModal.open({
    modalId: MODAL_ID,
    fullScreen: true,
    withCloseButton: false,
    padding: 0,
    transitionProps: FIXED_CHILDREN_MODAL_TRANSITION,
    children: <NodeMaintenanceModal node={node} serviceIssueId={serviceIssueId} />,
    styles: {
      content: {
        marginLeft: 'env(safe-area-inset-left)',
        marginRight: 'env(safe-area-inset-right)',
        boxShadow: 'none',
      },
    },
  })
}

type NodeMaintenanceModalProps = {
  node: TActiveNode | TActiveGateway
  serviceIssueId?: number
}

export const NodeMaintenanceModal: React.FC<NodeMaintenanceModalProps> = ({
  node,
  serviceIssueId: initialSelectedServiceIssueId = null,
}) => {
  const entityIdentifier = (node as TActiveNode).nodeIdentifier || (node as TActiveGateway).gatewayIdentifier
  const equipmentTypeLabel = getNodeTypeLabelFromNode(node)

  const [selectedServiceIssueId, setSelectedServiceIssueId] = useState<number | null>(
    initialSelectedServiceIssueId,
  )

  const allServiceIssues = serviceCenterStore.useSelector(serviceCenterStore.selectors.getAllServiceIssues)

  const [nodeServiceIssues, setNodeServiceIssues] = useState<TNodeServiceIssueSummary[]>(
    allServiceIssues[entityIdentifier] || [],
  )

  const selectedServiceIssue = nodeServiceIssues.find(
    (issue) => issue.serviceIssueId === selectedServiceIssueId,
  )

  const handleOnClose = () => {
    if (selectedServiceIssue) {
      return setSelectedServiceIssueId(null)
    } else {
      AboveAllModal.close(MODAL_ID)
    }
  }

  let title = translate.phrases.placeholder('{{equipmentType}} Maintenance', {
    equipmentType: equipmentTypeLabel,
  })

  if (selectedServiceIssue) {
    title = translate.phrases.placeholder('View Issue')

    if (
      checkHasIssueUpdatePermission() &&
      (selectedServiceIssue.serviceIssueStatus === TServiceIssueStatus.CREATED ||
        selectedServiceIssue.serviceIssueStatus === TServiceIssueStatus.FLAGGED_FOR_SERVICE)
    ) {
      title = translate.phrases.placeholder('Resolve Issue')
    }
  }

  const loadServiceIssues = useCallback(async (nodeIdentifier: string) => {
    try {
      const nodeServiceIssues = await serviceCenterNodeServiceIssuesGet({
        entityIdentifiers: [nodeIdentifier],
        propertyIds: [(node as TActiveNode).propertyId],
      })

      setNodeServiceIssues(nodeServiceIssues[nodeIdentifier] || [])
    } catch (error) {
      captureFrontendError(error)

      showNotification({
        type: 'error',
        message: translate.phrases.placeholder('Error: could not load service issues for the selected node!'),
      })
    }
  }, [])

  useEffect(() => {
    loadServiceIssues(entityIdentifier)
  }, [entityIdentifier])

  return (
    <>
      <WideHeader
        DropdownMenuItems={<DropdownMenuItems node={node} />}
        title={title}
        onClose={handleOnClose}
        isSecondaryModal={!!selectedServiceIssue}
      />

      {selectedServiceIssue ? (
        <ServiceIssueDetail
          serviceIssueId={selectedServiceIssue.serviceIssueId}
          issueTypeId={selectedServiceIssue.nodeServiceIssueTypeId}
          onIssueChange={() => loadServiceIssues(entityIdentifier)}
        />
      ) : (
        <ServiceIssueList
          node={node}
          serviceIssues={nodeServiceIssues}
          onSelectIssue={setSelectedServiceIssueId}
        />
      )}
    </>
  )
}

const DropdownMenuItems: FC<{ node: TActiveNode | TActiveGateway }> = ({
  node,
}: {
  node: TActiveNode | TActiveGateway
}) => {
  return (
    <>
      <Menu.Item
        onClick={() => {
          openNodeSwapModal()
        }}
      >
        {translate.phrases.placeholder('Swap Node')}
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          openNodeRemoveModal(node)
        }}
      >
        {translate.phrases.placeholder('Remove Node')}
      </Menu.Item>
    </>
  )
}
